<script>
  /**
   * RenameModal — a generic, reusable rename dialog.
   *
   * Accepts any object via `data` and delegates the actual save call to the
   * caller-supplied `onUpdate` async function. Handles all client-side
   * validation (empty, unchanged, duplicate, format sanitization) so each
   * consumer only needs to implement the API call.
   *
   * @example
   * <RenameModal
   *   bind:show={showModal}
   *   data={item}
   *   name={item.rulename}
   *   existingNames={items.map(i => i.rulename)}
   *   onUpdate={async (item, newName) => { await api.rename(item, newName); }}
   * />
   */

  import { format } from 'svelte-i18n';
  import { Button, Input, Modal } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { sanitizeInputValue } from '@client/utils/sanitize';

  import { isValidRuleName } from '@common/utils/validation';

  /**
   * Controls whether the modal is open. Use `bind:show` to let the modal
   * close itself when the user clicks the × button.
   * @type {boolean}
   */
  export let show = false;

  /**
   * The full data object being renamed. Passed as-is to `onUpdate` so the
   * caller has all context needed to perform the API call.
   * @type {Object}
   */
  export let data = {};

  /**
   * The current name of the object. Pre-fills the input and is used to
   * detect unchanged-name submissions and skip duplicate-name checks for
   * the current value.
   * @type {string}
   */
  export let name = '';

  /**
   * Optional list of already-taken names. When provided, submitting a value
   * that appears in this array (and is not equal to `name`) shows a
   * duplicate-name validation error and blocks saving.
   * @type {string[]}
   */
  export let existingNames = [];

  /**
   * Optional override for the duplicate-name validation error message.
   * Defaults to `label.RENAME_MODAL.ERROR.DUPLICATE`.
   * Pass an already-formatted string (e.g. from `$format(...)`) when the
   * entity type needs a more specific message (e.g. "A group with this name
   * already exists").
   * @type {string}
   */
  export let duplicateError = '';

  /**
   * Async callback invoked with `(data, newName)` when the user confirms.
   * The caller is responsible for the API call, closing the modal, and
   * showing any success / error notifications.
   * @type {(data: Object, newName: string) => Promise<void>}
   */
  export let onUpdate = async () => {};

  /** The value currently in the input field. Seeded from `name` on open. */
  let newName = '';

  /** Active inline validation error string, or empty when valid. */
  let error = '';

  /** True while `onUpdate` is awaiting, disables the Save button. */
  let updating = false;

  /**
   * Becomes true on the first keyup, enabling the empty-name error so we
   * don't show an error before the user has interacted with the field.
   */
  let touched = false;

  /**
   * Validates the current input value and returns a localised error string,
   * or an empty string when the value is acceptable.
   *
   * Rules (in order):
   *  1. Empty after being touched → EMPTY error
   *  2. Matches an existing name (excluding the current name) → DUPLICATE error
   *
   * @param {string} value
   * @returns {string}
   */
  const validate = (value) => {
    if (touched && !value) {
      return $format('label.RENAME_MODAL.ERROR.EMPTY');
    }
    if (value && value.length < 3) {
      return $format('label.VALIDATION_TOO_SHORT', { values: { param: $format('label.NAME'), length: 3 } });
    }
    if (value && !isValidRuleName(value)) {
      return $format('label.VALIDATION_ERROR_RULENAME');
    }
    if (value && value !== name && existingNames.includes(value)) {
      return duplicateError || $format('label.RENAME_MODAL.ERROR.DUPLICATE');
    }
    return '';
  };

  /**
   * Fired on every keyup inside the input.
   * Marks the field as touched, sanitizes the raw value in-place
   * (lowercase, spaces/hyphens → `_`, special chars stripped), then
   * re-validates.
   */
  const onKeyUp = () => {
    touched = true;
    newName = sanitizeInputValue(newName);
    error = validate(newName);
  };

  /** Resets all local state to its initial values. Called on modal close. */
  const resetModal = () => {
    newName = '';
    error = '';
    updating = false;
    touched = false;
  };

  /**
   * Invokes `onUpdate(data, newName)` and manages the `updating` flag so
   * the Save button is disabled for the duration of the async operation.
   */
  const onSave = async () => {
    updating = true;
    try {
      await onUpdate(data, newName);
    } finally {
      updating = false;
    }
  };

  // Seed / reset reactive state when the modal opens.
  $: if (show) {
    newName = name;
    error = '';
    touched = false;
  }

  // Reset local state when the modal closes.
  $: show === false && resetModal();

  // Save is only allowed when the name has actually changed and is valid.
  $: isDisabled = !newName || !!error || newName === name || updating;
</script>

<Modal bind:isOpen={show} size="sm">
  <div class="rename-modal">
    <button class="close" on:click={() => (show = false)}>
      <Icon name="x" />
    </button>

    <h1>{$format('label.RENAME')} <b>{name}</b></h1>

    <div class="rename-modal--field">
      <Input
        type="text"
        bind:value={newName}
        on:keyup={onKeyUp}
        placeholder={name}
        invalid={!!error}
        feedback={error}
      />
    </div>

    <div class="footer-actions">
      <Button color="primary" disabled={isDisabled} on:click={onSave}>
        {$format('label.SAVE')}
      </Button>
    </div>
  </div>
</Modal>

<style lang="scss" src="./rename-modal.scss"></style>
